import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { IEmailTheme } from "../../../../models/IEmailDocument";

type Props = {
    theme: IEmailTheme;
    onChange: (theme: IEmailTheme) => void;
    disabled?: boolean;
};

const FONT_FAMILIES = [
    "Arial, Helvetica, sans-serif",
    "Helvetica, Arial, sans-serif",
    "Verdana, Geneva, sans-serif",
    "Tahoma, Geneva, sans-serif",
    "'Trebuchet MS', Helvetica, sans-serif",
    "Georgia, 'Times New Roman', serif",
    "'Times New Roman', Times, serif",
    "'Courier New', Courier, monospace",
];

const FONT_SIZE = { min: 10, max: 32, fallback: 14 };
const CONTENT_WIDTH = { min: 320, max: 800, fallback: 600 };

const clampNumber = (raw: string, bounds: { min: number; max: number; fallback: number }): number => {
    const n = Number(raw);

    return Math.min(bounds.max, Math.max(bounds.min, n || bounds.fallback));
};

const COLOR_FIELDS: { key: keyof IEmailTheme; labelKey: string }[] = [
    { key: "backgroundColor", labelKey: "theme_background_color" },
    { key: "contentBackgroundColor", labelKey: "theme_content_background_color" },
    { key: "textColor", labelKey: "theme_text_color" },
    { key: "brandColor", labelKey: "theme_brand_color" },
    { key: "linkColor", labelKey: "theme_link_color" },
];

/** Document-wide colors, font and width. Email-safe font stacks only. */
const ThemePanel = (props: Props) => {
    const intl = useIntl();
    const set = (patch: Partial<IEmailTheme>) => props.onChange({ ...props.theme, ...patch });

    // Numeric inputs keep raw text while typing (clamping on every keystroke swallows partial values);
    // the clamped value is committed on blur.
    const [fontSizeText, setFontSizeText] = useState(String(props.theme.fontSize));
    const [contentWidthText, setContentWidthText] = useState(String(props.theme.contentWidth));

    useEffect(() => setFontSizeText(String(props.theme.fontSize)), [props.theme.fontSize]);
    useEffect(() => setContentWidthText(String(props.theme.contentWidth)), [props.theme.contentWidth]);

    const commitFontSize = () => {
        const fontSize = clampNumber(fontSizeText, FONT_SIZE);
        setFontSizeText(String(fontSize));
        if (fontSize !== props.theme.fontSize) {
            set({ fontSize });
        }
    };

    const commitContentWidth = () => {
        const contentWidth = clampNumber(contentWidthText, CONTENT_WIDTH);
        setContentWidthText(String(contentWidth));
        if (contentWidth !== props.theme.contentWidth) {
            set({ contentWidth });
        }
    };

    return (
        <div className={"ThemePanel"}>
            <div className={"theme-grid"}>
                {COLOR_FIELDS.map(({ key, labelKey }) => (
                    <div key={key}>
                        <label className={"form-label small mb-0"} htmlFor={`theme-${key}`}>
                            {intl.formatMessage({ id: labelKey })}
                        </label>
                        <div className={"d-flex align-items-center gap-2"}>
                            <input
                                id={`theme-${key}`}
                                type={"color"}
                                className={"form-control form-control-color"}
                                value={String(props.theme[key])}
                                disabled={props.disabled}
                                onChange={(e) => set({ [key]: e.target.value } as Partial<IEmailTheme>)}
                            />
                            <code className={"small"}>{String(props.theme[key])}</code>
                        </div>
                    </div>
                ))}
                <div>
                    <label className={"form-label small mb-0"} htmlFor={"theme-fontFamily"}>
                        {intl.formatMessage({ id: "theme_font_family" })}
                    </label>
                    <select
                        id={"theme-fontFamily"}
                        className={"form-select form-select-sm"}
                        value={props.theme.fontFamily}
                        disabled={props.disabled}
                        onChange={(e) => set({ fontFamily: e.target.value })}>
                        {!FONT_FAMILIES.includes(props.theme.fontFamily) ? (
                            <option value={props.theme.fontFamily}>{props.theme.fontFamily}</option>
                        ) : null}
                        {FONT_FAMILIES.map((f) => (
                            <option key={f} value={f}>
                                {f.split(",")[0].replace(/'/g, "")}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={"form-label small mb-0"} htmlFor={"theme-fontSize"}>
                        {intl.formatMessage({ id: "theme_font_size" })}
                    </label>
                    <input
                        id={"theme-fontSize"}
                        type={"number"}
                        min={FONT_SIZE.min}
                        max={FONT_SIZE.max}
                        className={"form-control form-control-sm"}
                        value={fontSizeText}
                        disabled={props.disabled}
                        onChange={(e) => setFontSizeText(e.target.value)}
                        onBlur={commitFontSize}
                    />
                </div>
                <div>
                    <label className={"form-label small mb-0"} htmlFor={"theme-contentWidth"}>
                        {intl.formatMessage({ id: "theme_content_width" })}
                    </label>
                    <input
                        id={"theme-contentWidth"}
                        type={"number"}
                        min={CONTENT_WIDTH.min}
                        max={CONTENT_WIDTH.max}
                        step={10}
                        className={"form-control form-control-sm"}
                        value={contentWidthText}
                        disabled={props.disabled}
                        onChange={(e) => setContentWidthText(e.target.value)}
                        onBlur={commitContentWidth}
                    />
                </div>
            </div>
        </div>
    );
};

export default ThemePanel;
